/**
 * `sign_in` — in-chat Microsoft sign-in for MCP hosts.
 *
 * MCPB/one-click installs configure everything through the host app's settings
 * UI, but authentication used to require a terminal (`--setup`/`--login`).
 * This tool closes that gap: it starts the standard Microsoft device-code
 * flow and returns the code + link IN THE CHAT for the user to complete in
 * their browser. Calling the tool again reports completion.
 *
 * Security posture:
 * - Display-only: the tool shows the Microsoft-issued device code; it never
 *   asks for, accepts, or sees credentials. Sign-in happens entirely on
 *   microsoft.com in the user's own browser, MFA included.
 * - Rate-limited: at most one new flow per minute, one pending flow at a time,
 *   so a runaway (or prompt-injected) session can't spam sign-in attempts.
 * - No console output: everything is returned as the tool result (stdout is
 *   the MCP protocol channel).
 */

import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import type { AuthenticationResult } from "@azure/msal-node";
import { PublicClientApplication } from "@azure/msal-node";
import { z } from "zod";
import { createPersistentCachePlugin } from "../auth/token-cache.js";
import { saveAccount, loadAccount } from "../auth/account-cache.js";
import { RESOURCES } from "../auth/types.js";
import { SCOPES, DEFAULT_AUTHORITY } from "../utils/constants.js";
import { sanitizeErrorMessage, tenantAuthorityFor, validateTenantId } from "../utils/security.js";
import { isInteractionRequiredError } from "../utils/errors.js";
import { createChildLogger } from "../utils/logger.js";
import {
  claimsBindingsMatch,
  hasPendingClaimsChallenge,
  takePendingClaimsChallenge,
  validateClaimsBinding,
  type PowerPlatformClaimsBinding,
} from "../auth/claims-challenge-store.js";

const signInLogger = createChildLogger({ module: "sign-in-tool" });

const MIN_MS_BETWEEN_FLOWS = 60_000;
const SIGN_IN_RESOURCES = ["flow", "power_platform_api"] as const;
const COPILOT_PERMISSIONS = [
  "ResourceQuery.Resources.Read",
  "CopilotStudio.Copilots.Invoke",
  "CopilotStudio.MakerOperations.Read",
  "CopilotStudio.MakerOperations.ReadWrite",
  "CopilotStudio.AdminActions.Invoke",
] as const;

export const signInInputSchema = z.object({
  resource: z.enum(SIGN_IN_RESOURCES).optional(),
  forceRefresh: z.boolean().optional(),
});

type SignInResource = (typeof SIGN_IN_RESOURCES)[number];

function scopesFor(resource: SignInResource): string[] {
  return resource === "power_platform_api"
    ? [`${RESOURCES.POWER_PLATFORM_API}/.default`]
    : [...SCOPES.FLOW];
}

function resourceLabel(resource: SignInResource): string {
  return resource === "power_platform_api" ? "Power Platform API" : "Power Automate (Flow)";
}

export const signInTool: Tool = {
  name: "sign_in",
  description:
    "Sign in to Microsoft Power Platform when other tools report missing credentials. " +
    "Starts Microsoft's device-code sign-in and returns a code plus a microsoft.com link for the user " +
    "to complete in their own browser (MFA included) — no terminal needed. Call sign_in again after " +
    "the user says they've finished to confirm. This tool only displays the code; it never asks for " +
    "or accepts passwords. Optionally choose resource=power_platform_api for Copilot Studio/API " +
    "authentication and forceRefresh=true after an administrator changes consent. Refreshing only " +
    "requests a new token for the selected resource; it does not change app permissions, grant consent, " +
    "or clear other cached tokens. Authentication does not prove access to every API.",
  inputSchema: {
    type: "object" as const,
    properties: {
      resource: {
        type: "string",
        enum: [...SIGN_IN_RESOURCES],
        description:
          "Token audience to check or sign in to. Defaults to flow; power_platform_api checks Copilot Studio's separate audience.",
      },
      forceRefresh: {
        type: "boolean",
        description:
          "Bypass the selected audience's cached access token (default false). Does not alter permissions or clear caches; sign-in may still be required.",
      },
    },
  },
};

interface PendingFlow {
  userCode: string;
  verificationUri: string;
  startedAtMs: number;
  expiresAtMs: number;
  done: boolean;
  account?: string;
  error?: string;
  resource: SignInResource;
  explicitRequest: boolean;
  scopes?: string[];
  /** This flow carries a Conditional Access claims challenge. */
  claimsChallenge?: boolean;
  claimsBinding?: PowerPlatformClaimsBinding;
}

// Module-level: one flow per server process.
let pending: PendingFlow | null = null;
let lastFlowStartMs = 0;
// Reserve the gap before Microsoft's device-code callback creates PendingFlow.
let startingClaimsFlow: symbol | null = null;

/** Test hook — reset module state between tests. */
export function resetSignInStateForTests(): void {
  pending = null;
  lastFlowStartMs = 0;
  startingClaimsFlow = null;
}

export interface SignInAuthConfig {
  clientId: string;
  tenantId: string | null;
  /** Server-owned snapshot of the app and selected account; never caller-supplied tool input. */
  getPowerPlatformClaimsBinding?: () => PowerPlatformClaimsBinding | null;
}

function formatCodeReply(flow: PendingFlow, lead: string): string {
  const minutesLeft = Math.max(1, Math.round((flow.expiresAtMs - Date.now()) / 60_000));
  return [
    lead,
    ...(flow.explicitRequest ? [`Requested resource: ${resourceLabel(flow.resource)}.`] : []),
    "",
    `1. Open: ${flow.verificationUri}`,
    `2. Enter code: **${flow.userCode}**`,
    "3. Sign in with your Microsoft work account (MFA works normally).",
    "",
    `The code expires in about ${minutesLeft} minute${minutesLeft === 1 ? "" : "s"}. ` +
      "Tell me when you've finished and I'll confirm the sign-in.",
  ].join("\n");
}

/**
 * Try a silent token acquisition against the shared cache to see whether the
 * user is already signed in. Uses its own PCA so it can't disturb the server's
 * main provider.
 */
async function probeExistingSignIn(
  auth: SignInAuthConfig,
  resource: SignInResource,
  forceRefresh: boolean,
  explicitRequest: boolean,
): Promise<{ account: string; scopes?: string[] } | null> {
  try {
    const pca = await buildPca(auth);
    const accounts = await pca.getTokenCache().getAllAccounts();
    // Explicit resource checks must target the same selected account the main
    // provider loads, not whichever account MSAL happens to enumerate first.
    // Keep the legacy no-argument probe unchanged.
    const account = (explicitRequest ? loadAccount() : null) ?? accounts[0];
    if (!account) return null;
    const result = await pca.acquireTokenSilent({
      account,
      scopes: scopesFor(resource),
      ...(forceRefresh ? { forceRefresh: true } : {}),
      ...(explicitRequest
        ? { authority: tenantAuthorityFor(auth.tenantId ?? account.tenantId, DEFAULT_AUTHORITY) }
        : {}),
    });
    if (!result) return null;
    return {
      account: result.account?.username ?? account.username ?? "your Microsoft account",
      scopes: result.scopes,
    };
  } catch (err) {
    // Preserve the old no-argument sign-in path. A resource-specific refresh
    // must not hide storage/network/config errors by starting another login.
    if (explicitRequest && !isInteractionRequiredError(err)) throw err;
    return null;
  }
}

/** AuthenticationResult.scopes is metadata, not permission verification. Never return tokens. */
function formatResourceResult(
  resource: SignInResource,
  account: string,
  scopes: string[] | undefined,
  lead: string,
): string {
  // Account names are normally emails, intentionally shown as in the original
  // sign-in UI. Keep arbitrary markup/control characters out of this field.
  const accountLabel = account.replace(/[\p{Cc}\p{Cf}*`<>[\]]/gu, "").slice(0, 254);
  const lines = [`${lead} ${resourceLabel(resource)} token for **${accountLabel}**.`];
  if (resource === "power_platform_api") {
    const knownScopes = new Set(
      (scopes ?? []).map((scope) => scope.replace(`${RESOURCES.POWER_PLATFORM_API}/`, "")),
    );
    // MSAL can fall back to the requested .default when response scope metadata
    // is absent. That is unknown membership, not proof of all/zero permissions.
    if (knownScopes.size === 0 || knownScopes.has(".default")) {
      lines.push(
        "Granted permission details were not returned; Copilot permissions are not verified.",
      );
    } else {
      const present = COPILOT_PERMISSIONS.filter((scope) => knownScopes.has(scope));
      const missing = COPILOT_PERMISSIONS.filter((scope) => !knownScopes.has(scope));
      lines.push(`Copilot-related permissions present: ${present.join(", ") || "none"}.`);
      if (missing.length) lines.push(`Not present in this token: ${missing.join(", ")}.`);
    }
    lines.push(
      "Refreshing cannot grant missing permissions. An authorized administrator must configure and consent " +
        "to any required permissions first; then call sign_in with resource=power_platform_api and forceRefresh=true.",
    );
  }
  lines.push(
    "Authentication only — API access and user roles are not verified. Retry the specific tool to check access.",
  );
  return lines.join("\n\n");
}

/** Entra/MSAL errors can contain credentials. Keep only a bounded, redacted diagnostic. */
function safeSignInError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return sanitizeErrorMessage(
    message.replace(/\bBearer\s+[A-Za-z0-9._~+/-]+={0,2}/gi, "Bearer [REDACTED]"),
  )
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[REDACTED]")
    .replace(
      /((?:access[_-]?token|refresh[_-]?token|id[_-]?token|client[_-]?secret|password)["']?\s*[:=]\s*["']?)[^\s,"'&}]+/gi,
      "$1[REDACTED]",
    )
    .replace(/[\p{Cc}\p{Cf}]/gu, " ")
    .slice(0, 1_000);
}

/** Claims-bearing SDK errors may echo raw or encoded claims. Never return or log their text. */
function safeClaimsSignInError(error: unknown): string {
  const message = error instanceof Error ? error.message.slice(0, 8192) : "";
  const code =
    /\bAADSTS(?:53000|53001|53002|53003|530036|50076|50079|65001|70043|50173|700082)\b/.exec(
      message,
    )?.[0];
  return (
    "Claims-bearing sign-in did not complete or could not be safely saved" +
    (code ? ` (${code})` : "") +
    ". Retry the original read-only tool to obtain a fresh challenge before calling sign_in again. " +
    "No Conditional Access settings or permissions were changed."
  );
}

function currentClaimsBinding(auth: SignInAuthConfig): PowerPlatformClaimsBinding | null {
  try {
    const binding = auth.getPowerPlatformClaimsBinding?.();
    if (
      !validateClaimsBinding(binding) ||
      binding.clientId.toLowerCase() !== auth.clientId.toLowerCase()
    )
      return null;
    if (
      auth.tenantId &&
      !["organizations", "common"].includes(auth.tenantId.toLowerCase()) &&
      auth.tenantId.toLowerCase() !== binding.account.tenantId.toLowerCase()
    )
      return null;
    return binding;
  } catch {
    return null;
  }
}

function isolatedClaimsPca(binding: PowerPlatformClaimsBinding): PublicClientApplication {
  // No persistence plugin: even a wrong-account response stays in this private
  // in-memory cache until the result and current binding have been verified.
  return new PublicClientApplication({
    auth: {
      clientId: binding.clientId,
      authority: `https://login.microsoftonline.com/${binding.account.tenantId}`,
    },
    system: { loggerOptions: { piiLoggingEnabled: false, loggerCallback: () => {} } },
  });
}

async function buildPca(auth: SignInAuthConfig): Promise<PublicClientApplication> {
  const validatedTenant = auth.tenantId ? validateTenantId(auth.tenantId) : null;
  const authority = validatedTenant
    ? `https://login.microsoftonline.com/${validatedTenant}`
    : DEFAULT_AUTHORITY;
  const cachePlugin = await createPersistentCachePlugin();
  return new PublicClientApplication({
    auth: { clientId: auth.clientId, authority },
    cache: { cachePlugin },
  });
}

export async function handleSignIn(auth: SignInAuthConfig, input: unknown): Promise<string> {
  const parsed = signInInputSchema.parse(input ?? {});
  const resource = parsed.resource ?? "flow";
  const forceRefresh = parsed.forceRefresh ?? false;
  const explicitRequest = parsed.resource !== undefined || forceRefresh;
  if (startingClaimsFlow) {
    return "A Power Platform API claims sign-in is starting. Call sign_in again for its code or result; no additional sign-in was started.";
  }
  // A parked challenge means a same-context silent attempt failed. This explicit
  // sign-in may attempt interaction, but neither MFA deficiency nor policy
  // satisfaction can be inferred. Only the challenged audience qualifies.
  const redeemChallenge = resource === "power_platform_api" && hasPendingClaimsChallenge();
  // A flow is in flight (or just finished) — report its state.
  if (pending) {
    if (pending.done) {
      const finished = pending;
      pending = null;
      if (finished.error) {
        return (
          `The sign-in didn't complete: ${finished.error}\n\n` +
          (finished.claimsChallenge
            ? "Retry the original read-only tool for a fresh challenge."
            : "Ask me to sign in again to get a fresh code.")
        );
      }
      if (
        finished.claimsChallenge &&
        !claimsBindingsMatch(finished.claimsBinding, currentClaimsBinding(auth))
      ) {
        return safeClaimsSignInError(new Error("Selected authentication context changed"));
      }
      if (finished.explicitRequest || explicitRequest) {
        const mismatch =
          resource !== finished.resource && parsed.resource !== undefined
            ? `\n\nThis completed ${resourceLabel(finished.resource)} sign-in, not ${resourceLabel(resource)}. Call sign_in again for the requested resource.`
            : "";
        const policyNote = finished.claimsChallenge
          ? "\n\nA claims-bearing token was acquired for the same selected account. " +
            "The earlier API access is not verified; retry the original tool to check it."
          : "";
        return (
          formatResourceResult(
            finished.resource,
            finished.account ?? "your Microsoft account",
            finished.scopes,
            "Acquired",
          ) +
          mismatch +
          policyNote
        );
      }
      return (
        `Signed in as **${finished.account ?? "your Microsoft account"}**. ` +
        "Power Platform tools are ready to use."
      );
    }
    if (Date.now() > pending.expiresAtMs) {
      pending = null;
      // fall through to start a fresh flow (rate limit still applies)
    } else {
      return formatCodeReply(
        pending,
        `A sign-in is already in progress for ${resourceLabel(pending.resource)} — here are the details again:`,
      );
    }
  }

  const binding = redeemChallenge ? currentClaimsBinding(auth) : null;
  if (redeemChallenge && (!binding || !hasPendingClaimsChallenge(binding))) {
    return (
      "The pending claims challenge does not match the configured app and current selected account. " +
      "No new sign-in was started. Retry the original read-only tool using the intended existing session."
    );
  }

  // No pending flow: if the cache already works, say so instead of starting one.
  let existing: Awaited<ReturnType<typeof probeExistingSignIn>>;
  try {
    existing = redeemChallenge
      ? null
      : await probeExistingSignIn(auth, resource, forceRefresh, explicitRequest);
  } catch (err) {
    return (
      `Could not ${forceRefresh ? "refresh" : "check"} the ${resourceLabel(resource)} token: ${safeSignInError(err)}\n\n` +
      "No new sign-in was started and no cached credentials were cleared. Resolve this error before retrying."
    );
  }
  if (existing) {
    if (explicitRequest) {
      return formatResourceResult(
        resource,
        existing.account,
        existing.scopes,
        forceRefresh ? "Refreshed" : "Acquired",
      );
    }
    return (
      `Already signed in as **${existing.account}** — no sign-in needed. ` +
      "If you want to switch accounts, run `powerautomate-mcp --login` from a terminal."
    );
  }

  if (Date.now() - lastFlowStartMs < MIN_MS_BETWEEN_FLOWS) {
    const wait = Math.ceil((MIN_MS_BETWEEN_FLOWS - (Date.now() - lastFlowStartMs)) / 1000);
    return `A sign-in was started less than a minute ago. Wait ${wait}s and ask again.`;
  }

  // Reserve the exact envelope atomically, before any awaited setup. Never
  // silently start an ordinary login if the challenge expires or is unavailable.
  const challenge = redeemChallenge && binding ? takePendingClaimsChallenge(binding) : null;
  if (redeemChallenge && !challenge)
    return safeClaimsSignInError(new Error("Challenge unavailable"));
  const isStillCurrent = () =>
    challenge !== null && claimsBindingsMatch(challenge.binding, currentClaimsBinding(auth));
  if (challenge && !isStillCurrent())
    return safeClaimsSignInError(new Error("Selected authentication context changed"));

  lastFlowStartMs = Date.now();
  const claimsAttempt = challenge ? Symbol("claims sign-in") : null;
  if (claimsAttempt) startingClaimsFlow = claimsAttempt;
  const clearStartingClaimsFlow = () => {
    if (claimsAttempt && startingClaimsFlow === claimsAttempt) startingClaimsFlow = null;
  };
  signInLogger.info("Starting in-chat device-code sign-in");

  let pca: PublicClientApplication;
  try {
    pca = challenge ? isolatedClaimsPca(challenge.binding) : await buildPca(auth);
  } catch (error) {
    clearStartingClaimsFlow();
    if (challenge) return safeClaimsSignInError(error);
    throw error;
  }

  let resolveCode!: (value: PendingFlow) => void;
  let rejectCode!: (reason: Error) => void;
  const codeReady = new Promise<PendingFlow>((resolve, reject) => {
    resolveCode = resolve;
    rejectCode = reject;
  });
  let startedFlow: PendingFlow | null = null;

  // MSAL sends these validated claims on both device-code and token requests.
  const claims = challenge?.claims;

  const acquisition = pca.acquireTokenByDeviceCode({
    scopes: scopesFor(resource),
    ...(claims ? { claims } : {}),
    deviceCodeCallback: (response) => {
      const flow: PendingFlow = {
        userCode: response.userCode ?? "",
        verificationUri: response.verificationUri ?? "https://microsoft.com/devicelogin",
        startedAtMs: Date.now(),
        expiresAtMs: Date.now() + (response.expiresIn ?? 900) * 1000,
        done: false,
        resource,
        explicitRequest,
        ...(claims ? { claimsChallenge: true } : {}),
        ...(challenge ? { claimsBinding: challenge.binding } : {}),
      };
      if (!flow.userCode) {
        rejectCode(
          new Error(
            "Your organization didn't issue a device code — it may block device-code sign-in. " +
              "Run `powerautomate-mcp --setup` from a terminal for detailed diagnostics.",
          ),
        );
        return;
      }
      startedFlow = flow;
      pending = flow;
      clearStartingClaimsFlow();
      resolveCode(flow);
    },
  });

  // Background completion: record the outcome for the next sign_in call.
  void acquisition
    .then(async (result: AuthenticationResult | null) => {
      if (challenge) {
        if (
          !startedFlow ||
          pending !== startedFlow ||
          !isStillCurrent() ||
          !result?.accessToken ||
          typeof result.accessToken !== "string" ||
          !result.accessToken.trim() ||
          !claimsBindingsMatch(challenge.binding, { ...challenge.binding, account: result.account })
        ) {
          throw new Error(
            "Claims acquisition did not preserve the selected authentication context",
          );
        }
        const { persistVerifiedClaimsCache } = await import("../auth/claims-cache-persistence.js");
        await persistVerifiedClaimsCache(
          pca.getTokenCache(),
          challenge.binding,
          () => pending === startedFlow && isStillCurrent(),
        );
        if (pending !== startedFlow || !isStillCurrent())
          throw new Error("Selected authentication context changed");
      }
      // An expired flow can finish after a newer resource-specific flow starts.
      // Its completion must never mark the newer resource authenticated.
      if (startedFlow) {
        startedFlow.done = true;
        startedFlow.account = result?.account?.username ?? undefined;
        startedFlow.scopes = result?.scopes;
        if (!result)
          startedFlow.error =
            "Microsoft did not return an authentication result. Access is not verified.";
      }
      // Saving an old completion would switch the selected account back after
      // a newer sign-in succeeded (even after its result was already polled).
      if (!challenge && result?.account && startedFlow && pending === startedFlow) {
        saveAccount(result.account);
        signInLogger.info({ username: result.account.username }, "In-chat sign-in completed");
      }
    })
    .catch((err: unknown) => {
      clearStartingClaimsFlow();
      const message = challenge ? safeClaimsSignInError(err) : safeSignInError(err);
      if (startedFlow) {
        startedFlow.done = true;
        startedFlow.error = message;
      }
      signInLogger.warn({ err: message }, "In-chat sign-in failed or expired");
    });

  try {
    const flow = await Promise.race([
      codeReady,
      // If AAD rejects before issuing a code (bad client id, blocked flow),
      // surface that instead of waiting forever.
      acquisition.then(() => {
        throw new Error("Sign-in completed before a code was displayed — try your tools now.");
      }),
    ]);
    return formatCodeReply(
      flow,
      claims
        ? "Attempt the additional claims request through device-code sign-in" +
            (challenge?.context ? ` (${challenge.context})` : "") +
            ". This does not establish that MFA was missing, and policy may still deny access:"
        : "Let's get you signed in to Power Platform:",
    );
  } catch (err) {
    clearStartingClaimsFlow();
    if (pending === startedFlow) pending = null;
    return `Couldn't start the sign-in: ${challenge ? safeClaimsSignInError(err) : safeSignInError(err)}`;
  }
}

/** Whether an account is known (used to decide if sign_in guidance is worth appending). */
export function hasKnownAccount(): boolean {
  return loadAccount() !== null;
}
